import { Box, BoxProps, Flex, Spinner, VStack } from "@chakra-ui/react";
import type { FC } from "react";
import { type DropzoneOptions as ReactDropzoneOptions, useDropzone } from "react-dropzone";
export type * from "react-dropzone";

import { Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

type DropzoneProps = {
  icon?: React.ReactNode;
  zoneText?: string | string[];
  boxProps?: BoxProps;
  isLoading?: boolean;
  responsive?: boolean;
};

export const Dropzone: FC<DropzoneProps & ReactDropzoneOptions> = ({
  zoneText = "Add New File",
  icon = <Icon.Plus size="1.2em" />,
  multiple = false,
  onDrop = () => {},
  boxProps = {},
  isLoading = false,
  responsive = false,
  ...rest
}) => {
  const { getRootProps, getInputProps } = useDropzone({
    multiple,
    onDrop,
    useFsAccessApi: false,
    noClick: true,
    noKeyboard: true,
    ...rest,
  });

  return (
    <Box
      as="label"
      {...getRootProps()}
      w="100%"
      minH={responsive ? ["60px", "80px"] : undefined}
      h={responsive ? ["60px", "80px"] : undefined}
      borderWidth="1px"
      borderStyle="dashed"
      borderRadius="lg"
      borderColor="secondary.500"
      p={4}
      textAlign="center"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: "secondary.600",
        bg: "secondary.50",
      }}
      _active={{
        bg: "secondary.100",
      }}
      {...boxProps}>
      <input {...getInputProps({ style: { display: "none" } })} />
      {isLoading ? (
        <Spinner color="secondary.500" />
      ) : (
        <Flex color="secondary.500" alignItems="center" justifyContent="center">
          {Array.isArray(zoneText) ? (
            <VStack gap={3} mr={5}>
              {zoneText.map((text, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: Filenames may be the same
                <Text key={`${text}-${index}`}>{text}</Text>
              ))}
            </VStack>
          ) : (
            <Text m={2}>{zoneText}</Text>
          )}
          {icon ?? <Icon.Plus size="1.2em" />}
        </Flex>
      )}
    </Box>
  );
};
